// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React, { useId } from 'react';
import { Radio } from './Radio';
import './RadioGroup.css';

export interface RadioGroupOption {
  value: string;
  text: string;
  disabled?: boolean;
}

export interface RadioGroupProps {
  /** Shared name binding the radios into one group. Auto-generated when omitted. */
  name?: string;
  label?: string;
  options: RadioGroupOption[];
  value?: string;
  onChange?: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
  /** Lay the radios out in a row instead of stacked. */
  inline?: boolean;
  size?: 'md' | 'sm';
  error?: string;
  fluid?: boolean;
  className?: string;
  'data-testid'?: string;
}

/**
 * A single-select group of {@link Radio} controls sharing one {@code name},
 * with the standard field label + required marker (mirrors {@link Select}).
 * Emits the selected option's value through {@code onChange}.
 */
export const RadioGroup: React.FC<RadioGroupProps> = ({
  name,
  label,
  options,
  value,
  onChange,
  required,
  disabled,
  inline = false,
  size = 'md',
  error,
  fluid = false,
  className = '',
  'data-testid': testId,
}) => {
  const autoName = useId();
  const groupName = name ?? autoName;

  const wrapperClasses = [
    'fin-radio-group',
    fluid && 'fin-radio-group--fluid',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const optionsClasses = [
    'fin-radio-group__options',
    inline && 'fin-radio-group__options--inline',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={wrapperClasses} role="radiogroup" aria-label={label} data-testid={testId}>
      {label && (
        <span className="fin-radio-group__label">
          {label}
          {required && <span className="fin-radio-group__required">*</span>}
        </span>
      )}
      <div className={optionsClasses}>
        {options.map((opt) => (
          <Radio
            key={opt.value}
            name={groupName}
            size={size}
            label={opt.text}
            value={opt.value}
            checked={value === opt.value}
            disabled={disabled || opt.disabled}
            onChange={(e) => {
              if (e.target.checked) onChange?.(opt.value);
            }}
          />
        ))}
      </div>
      {error && <p className="fin-radio-group__error" role="alert">{error}</p>}
    </div>
  );
};

RadioGroup.displayName = 'RadioGroup';
